import { quotaErrorMessage } from "@/billing"
import { launchEvaluationV1 as launchRun } from "@/runs"
import { getUserFacingErrorMessage } from "@coval/ui"

import type { LaunchRunV1Request } from "@/types/api/runs-api-v1"
import { formatMissingMetricDependenciesError } from "@/utils/metricDependencyErrors"

import type { LaunchResult } from "../types"

interface ExecuteEvaluationLaunchParams {
  agentIds: string[]
  personaIds: string[]
  testSetIds: string[]
  shouldSubSample: boolean
  selectedMutationIds: Record<string, string[]>
  metricLabelsById?: Record<string, string>
  generateLaunchRunRequest: (
    agentId: string,
    personaId: string,
    testSetId: string,
    sharedSeed?: number,
    mutationIds?: string[],
  ) => LaunchRunV1Request | null
  internalDevId?: string
  workspaceId?: string | null
  launchRunFn?: typeof launchRun
  wait?: (ms: number) => Promise<void>
  generateSharedSeed?: () => number
}

const defaultWait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms)
  })

type LaunchRunResponse =
  | {
      statusCode?: number
      run_id?: string
    }
  | string
  | null

export async function executeEvaluationLaunch({
  agentIds,
  personaIds,
  testSetIds,
  shouldSubSample,
  selectedMutationIds,
  metricLabelsById,
  generateLaunchRunRequest,
  internalDevId = "",
  workspaceId,
  launchRunFn = launchRun,
  wait = defaultWait,
  generateSharedSeed = () => Math.floor(Math.random() * 1000000),
}: ExecuteEvaluationLaunchParams): Promise<LaunchResult> {
  if (personaIds.length === 0) {
    return {
      success: false,
      runIds: [],
      error: "No personas selected",
    }
  }

  if (agentIds.length === 0) {
    return {
      success: false,
      runIds: [],
      error: "No agents selected",
    }
  }

  if (testSetIds.length === 0) {
    return {
      success: false,
      runIds: [],
      error: "No test sets selected",
    }
  }

  const results: LaunchResult = { success: true, runIds: [] }
  const sharedSubSampleSeed = shouldSubSample ? generateSharedSeed() : undefined
  const totalCombinations =
    agentIds.length * personaIds.length * testSetIds.length
  const needsStagger = totalCombinations > 1
  let isFirstLaunch = true

  for (const agentId of agentIds) {
    for (const personaId of personaIds) {
      for (const testSetId of testSetIds) {
        if (needsStagger && !isFirstLaunch) {
          await wait(1000)
        }
        isFirstLaunch = false

        try {
          const request = generateLaunchRunRequest(
            agentId,
            personaId,
            testSetId,
            sharedSubSampleSeed,
            selectedMutationIds[agentId] || [],
          )

          if (!request) {
            results.success = false
            results.error = `Failed to generate config for agent ${agentId}`
            break
          }

          const finalRequest = internalDevId
            ? { ...request, dev_id: internalDevId }
            : request

          const response = (await launchRunFn(
            finalRequest,
            workspaceId,
          )) as LaunchRunResponse

          if (
            typeof response === "object" &&
            response !== null &&
            response.statusCode === 200 &&
            response.run_id
          ) {
            results.runIds.push(response.run_id)
          } else {
            results.success = false
            results.error = getUserFacingErrorMessage(
              typeof response === "object" && response !== null
                ? { status: response.statusCode }
                : response,
              {
                fallback: "The evaluation did not start. Please try again.",
              },
            )
            break
          }
        } catch (error) {
          results.success = false
          results.error =
            formatMissingMetricDependenciesError(error, metricLabelsById) ??
            quotaErrorMessage(error) ??
            getUserFacingErrorMessage(error, {
              fallback: "Unable to launch the evaluation. Please try again.",
            })
          break
        }
      }

      if (!results.success) {
        break
      }
    }

    if (!results.success) {
      break
    }
  }

  return results
}
